import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { PensionOption } from "../../../../../../../Wrap/Constants/Enums/Apply/PensionOption";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CrystallisationOption } from "../../../../../../../Wrap/Constants/Enums/Drawdown/CrystallisationOption";
import { CrystallisationDetails } from "../../../../../../../Wrap/Models/Domains/Drawdown/CrystallisationDetails";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { SectionHeader } from "../../SharedComponents";
import {
    calculateUnCrystallizationAmount,
    hasProtectedRetireAgeOrSchemeTaxFreeCash,
    calculateAvailableCash,
    calculateAvailableToCrystallise,
} from "./Helper/DrawdownDetailHelper";
import {
    CrystallisationOptions,
    drawdownDetailsMessage,
    crystallisationAmountErrorMessage,
    CrystallisationOptionsMap,
} from "./Resource/Contents";
import { ChooseAssetsToSellAssets } from "./Store/DrawdownCalculationStoreStatic";
import { CustomerPensionProtectionModel } from '../../../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionProtectionModel';

interface ICrystallisationDetailsProps extends IProcessComponentProps {
    data: DrawdownModel;
    pensionProtection: CustomerPensionProtectionModel;
    processType: ProcessType;
    modelData: PensionBase;
    cashInstrmentProductId: number;
    doCalculation: VoidFunction;
    isImmediateTaxFreeLumpSum: boolean;
    assets?: ChooseAssetsToSellAssets[];
}

interface ICrystallisationDetailsState { }

export class CrystallisationDetailsSection extends ProcessComponent<
    ICrystallisationDetailsProps,
    ICrystallisationDetailsState
> {
    private didInitialCalculation: boolean = false;

    constructor(props: any) {
        super(props);
        this.state = {};
    }

   componentDidMount() {
       super.componentDidMount();
       const { data, isImmediateTaxFreeLumpSum } = this.props;
       const fullAmount = data?.CrystallisationDetails?.AvailableToCrystallise?.Amount;
       const isFullAmountCase = isImmediateTaxFreeLumpSum || this.props.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection;
       var defaultOption = this.getCrystallisationOptionDefaultValue(this.props.data?.CrystallisationDetails, fullAmount);
       this.onCrystallisationDetailChange({
           CrystallisationOption: this.props.data?.CrystallisationDetails?.CrystallisationOption ?? defaultOption,
           CrystallisationAmount: isFullAmountCase
               ? { Amount: fullAmount, Currency: CurrencyCode.GBP }
               : this.props.data?.CrystallisationDetails?.CrystallisationAmount ?? null
       });
    }

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        //Crystallisation Option update will trigger backend calculation. This is moved from component onBlur to here.
        //The effect is - once click Partial/Full button and after upper state option value udpated, the calculation happens. no need to wait onBlur event happen
        if (prevProps.data.CrystallisationDetails?.CrystallisationOption != null &&
            this.props.data.CrystallisationDetails?.CrystallisationOption != prevProps.data.CrystallisationDetails?.CrystallisationOption) {
            this.props.doCalculation();
        }

        if (this.props.data?.CrystallisationDetails?.CrystallisationOption
            && !this.didInitialCalculation) {
            this.props.doCalculation();
            this.didInitialCalculation = true;
        }

        if (prevProps?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection == null
            && this.props?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection
        ) {
            this.onCrystallisationDetailChange({
                CrystallisationOption: CrystallisationOption.Full,
                CrystallisationAmount: this.props.data?.CrystallisationDetails?.AvailableToCrystallise?.Amount
            });
        }

        if (prevProps?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection != null
            && this.props?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection != null
            && prevProps?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection != this.props?.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection
        ) {
            if (this.props.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection) {
                this.onCrystallisationDetailChange({
                    CrystallisationOption: CrystallisationOption.Full,
                    CrystallisationAmount: this.props.data?.CrystallisationDetails?.AvailableToCrystallise?.Amount
                });
            }
            else {
                this.onCrystallisationDetailChange({
                    CrystallisationOption: this.availableToCrystallise > this.props.data?.CrystallisationDetails?.CrystallisationAmount?.Amount ? CrystallisationOption.Partial : CrystallisationOption.Full
                });
            }
        }
    }

    private onCrystallisationDetailChange(changes) {
        this.onDataChange(this.props.onChange, changes, this.props.data, "CrystallisationDetails");
    }

    private availableToCrystallise = this.props.data.CrystallisationDetails?.AvailableToCrystallise?.Amount
    private getConfigTable = (CrystallisationDetails: CrystallisationDetails) => {
        const { data, modelData, processType, isImmediateTaxFreeLumpSum } = this.props;

        let config = [
            {
                title: "Savings pot value",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                        : CrystallisationDetails?.UnCrystallisedValue?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Value available to drawdown",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateAvailableToCrystallise(modelData, processType, isImmediateTaxFreeLumpSum)
                        : this.availableToCrystallise,
                    Currency: CurrencyCode.GBP,
                }),
            },
            {
                title: "Available cash",
                data: MoneyDisplayHelper.getMoneyFormat({
                    Amount: modelData
                        ? calculateAvailableCash(modelData, processType, isImmediateTaxFreeLumpSum)
                        : CrystallisationDetails?.AvailableCash?.Amount,
                    Currency: CurrencyCode.GBP,
                }),
            },
        ];
        if (![ProcessType.NB, ProcessType.Quote].includes(this.props.processType)) {
            config = [
                {
                    title: "Drawdown pot value",
                    data: MoneyDisplayHelper.getMoneyFormat(CrystallisationDetails?.CrystallisedValue),
                },
            ].concat(config);
        }
        return config;
    };


    private renderCrystallisationDetails(): JSX.Element {
        const { data, pensionProtection, isImmediateTaxFreeLumpSum, modelData, processType } = this.props;
        const { CrystallisationDetails } = data;
        const isUFPLS = data?.PensionOption == PensionOption.UFPLS;

        if (isImmediateTaxFreeLumpSum) {
            //this is no good to assign upper prop value during render - please do not add any more. 
            //If you are fixing isImmediateTaxFreeLumpSum + enter drawdown request id case, can consider to fix this. test case seen in SLP-162964
            if (processType === ProcessType.TakePensionBenefits && this.availableToCrystallise > CrystallisationDetails?.CrystallisationAmount?.Amount) {
                CrystallisationDetails.CrystallisationOption = CrystallisationOption.Partial;
            }
            else {
                CrystallisationDetails.CrystallisationOption = CrystallisationOption.Full;
            }
        }
        return (
            <>
                <Typography type="common" wrapCss="dt-bold" config={this.getConfigTable(CrystallisationDetails)} />
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Drawdown options"
                    disabled={
                        isImmediateTaxFreeLumpSum ||
                        this.availableToCrystallise <
                        CrystallisationDetails?.UnCrystallisedValue?.Amount ||
                        this.validateFullOption(
                            isUFPLS,
                            CrystallisationDetails,
                            modelData,
                            processType,
                            isImmediateTaxFreeLumpSum
                        ) == false
                    }
                    options={((isImmediateTaxFreeLumpSum && processType !== ProcessType.TakePensionBenefits) || (this.props.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection)) ? [CrystallisationOption.Full] : CrystallisationOptions}
                    getText={(v) => {
                        return CrystallisationOptionsMap[v];
                    }}
                    selected={CrystallisationDetails?.CrystallisationOption}
                    onChange={(v) => {
                        const amount = {
                            Amount:
                                v == CrystallisationOption.Full
                                    ? this.availableToCrystallise
                                    : 0,
                            Currency: CurrencyCode.GBP,
                        };
                        this.onCrystallisationDetailChange({
                            CrystallisationOption: v,
                            CrystallisationAmount: amount,
                        });
                    }}

                    alwaysValidateOnUpdate
                    validationRules={[
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Full
                                    ? true
                                    : v == CrystallisationOption.Partial &&
                                    !hasProtectedRetireAgeOrSchemeTaxFreeCash(pensionProtection);
                            },
                        },
                        {
                            errorMessage: drawdownDetailsMessage.crystallisationOptionFullErrorMessage,
                            validator: (v) => {
                                return v == CrystallisationOption.Partial
                                    ? true
                                    : v == CrystallisationOption.Full &&
                                    this.validateFullOption(
                                        isUFPLS,
                                        CrystallisationDetails,
                                        modelData,
                                        processType,
                                        isImmediateTaxFreeLumpSum
                                    );
                            },
                        },
                    ]}
                />
                <MoneyTextBoxEditor
                    cssWrapper="control-size-245"
                    label="Total Drawdown amount"
                    currency={CurrencyCode.GBP}
                    value={Number(this.getMoneyTextBoxValue(this.props).toFixed(2))}
                    disabled={
                        isImmediateTaxFreeLumpSum ||
                        CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full
                    }
                    required={"Please enter"}

                    onBlur={(v) => {
                        if (this.validateRulesForCrystallisation(v)) {
                            this.props.doCalculation();
                        }
                    }}
                    onChange={(v) =>
                        this.onCrystallisationDetailChange({
                            CrystallisationAmount: { Amount: v ? Number(v.toFixed(2)) : 0, Currency: CurrencyCode.GBP },
                        })
                    }
                    validationRules={[
                        {
                            errorMessage: crystallisationAmountErrorMessage.valueToCrystalliseIsZero,
                            validator: (v) => this.availableToCrystallise != 0,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndLessThanUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                    this.availableToCrystallise <
                                    CrystallisationDetails?.UnCrystallisedValue?.Amount
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputIsZeroAndEqualUncrystallised,
                            validator: (v) =>
                                v == 0 &&
                                    this.availableToCrystallise ==
                                    CrystallisationDetails?.UnCrystallisedValue?.Amount
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage: crystallisationAmountErrorMessage.inputEqualValueToCrystallise,
                            validator: (v) =>
                                v == this.availableToCrystallise
                                    && CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Partial
                                    && this.availableToCrystallise == CrystallisationDetails?.UnCrystallisedValue?.Amount
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanValueToCrystalliseAndLessThanUncrystallised,
                            validator: (v) =>
                                v > this.availableToCrystallise
                                    &&
                                    this.availableToCrystallise <=
                                    CrystallisationDetails?.UnCrystallisedValue?.Amount
                                    ? false
                                    : true,
                        },
                        {
                            errorMessage:
                                crystallisationAmountErrorMessage.inputGreaterThanAvailableCash,
                            validator: (v) =>
                                v <= CrystallisationDetails?.AvailableCash?.Amount
                                ||
                                data.PensionOption != PensionOption.UFPLS
                                ||
                                processType === ProcessType.WhatIf
                        },
                    ]}
                />
            </>
        );
    }

    private validateRulesForCrystallisation(value: number) {
        const { data, processType } = this.props;
        const { CrystallisationDetails } = data;
        const isUFPLS = data?.PensionOption == PensionOption.UFPLS;
        const isWhatIf = processType === ProcessType.WhatIf;
        if (
            this.availableToCrystallise != 0 &&
            CrystallisationDetails?.CrystallisationAmount?.Amount != 0 &&
            CrystallisationDetails?.CrystallisationAmount?.Amount <=
            this.availableToCrystallise &&
            !(
                value == this.availableToCrystallise &&
                CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Partial &&
                this.availableToCrystallise ==
                CrystallisationDetails?.UnCrystallisedValue?.Amount
            ) &&
            !(isUFPLS && !isWhatIf && CrystallisationDetails?.CrystallisationAmount?.Amount > CrystallisationDetails.AvailableCash?.Amount))
        {
            return true;
        }
        return false;
    }

    private validateFullOption(
        isUFPLS: boolean,
        data: CrystallisationDetails,
        modelData: PensionBase,
        processType: ProcessType,
        isImmediateTaxFreeLumpSum: boolean
    ): boolean {
        return (
            isImmediateTaxFreeLumpSum ||
            (data?.AvailableToCrystallise?.Amount ==
                (modelData
                    ? calculateUnCrystallizationAmount(modelData, processType, isImmediateTaxFreeLumpSum)
                    : data?.UnCrystallisedValue?.Amount)
            )
        );
    }

    private getCrystallisationOptionDefaultValue(data: CrystallisationDetails, amount: number): CrystallisationOption {
        if (this.props.isImmediateTaxFreeLumpSum) {
            return amount == this.availableToCrystallise
                ? CrystallisationOption.Full
                : CrystallisationOption.Partial;
        }

        if (this.props.pensionProtection?.PensionSchemeSpecificProtection?.HasProtection) {
            return amount == this.availableToCrystallise
                ? CrystallisationOption.Full
                : CrystallisationOption.Partial;
        }

        return CrystallisationOption.Partial;
    }

    private getMoneyTextBoxValue(props: ICrystallisationDetailsProps) {
        const { isImmediateTaxFreeLumpSum, data: { CrystallisationDetails }, processType } = props;
        return ((isImmediateTaxFreeLumpSum ||
            CrystallisationDetails?.CrystallisationOption == CrystallisationOption.Full) &&
            processType !== ProcessType.TakePensionBenefits)
            ? CrystallisationDetails?.AvailableToCrystallise?.Amount
            : CrystallisationDetails?.CrystallisationAmount?.Amount ?? 0;
    }

    public render(): JSX.Element {
        return (
            <>
                {SectionHeader("Drawdown details")}
                {this.renderCrystallisationDetails()}
            </>
        );
    }
}
